import { html, nothing } from "lit";
import type { ChannelAccountSnapshot, TelegramStatus } from "../types.ts";
import type { ChannelsProps } from "./channels.types.ts";
import { formatRelativeTimestamp } from "../format.ts";
import { renderChannelConfigSection } from "./channels.config.ts";
import { channelStatusChip, channelMeta } from "./channels.shared.ts";

export function renderTelegramCard(params: {
  props: ChannelsProps;
  telegram?: TelegramStatus;
  telegramAccounts: ChannelAccountSnapshot[];
  accountCountLabel: unknown;
}) {
  const { props, telegram, telegramAccounts, accountCountLabel } = params;
  const hasMultipleAccounts = telegramAccounts.length > 1;

  const renderAccountCard = (account: ChannelAccountSnapshot) => {
    const probe = account.probe as { bot?: { username?: string } } | undefined;
    const botUsername = probe?.bot?.username;
    const label = account.name || account.accountId;
    return html`
      <div class="account-card">
        <div class="account-card-header chan-head">
          <div class="account-card-title">
            ${botUsername ? `@${botUsername}` : label}
          </div>
          ${channelStatusChip({ configured: account.configured, running: account.running })}
        </div>
        <div class="account-card-id">${account.accountId}</div>
        ${channelMeta([
          {
            k: "Last inbound",
            v: account.lastInboundAt ? formatRelativeTimestamp(account.lastInboundAt) : null,
          },
        ])}
        ${
          account.lastError
            ? html`<div class="account-card-error">${account.lastError}</div>`
            : nothing
        }
      </div>
    `;
  };

  return html`
    <div class="card">
      <div class="chan-head">
        <div>
          <div class="card-title">Telegram</div>
          <div class="card-sub">Bot status and channel configuration.</div>
        </div>
        ${
          hasMultipleAccounts
            ? nothing
            : channelStatusChip({ configured: telegram?.configured, running: telegram?.running })
        }
      </div>
      ${accountCountLabel}

      ${
        hasMultipleAccounts
          ? html`
            <div class="account-card-list">
              ${telegramAccounts.map((account) => renderAccountCard(account))}
            </div>
          `
          : channelMeta([
              { k: "Mode", v: telegram?.mode ?? null },
              {
                k: "Started",
                v: telegram?.lastStartAt ? formatRelativeTimestamp(telegram.lastStartAt) : null,
              },
              {
                k: "Probed",
                v: telegram?.lastProbeAt ? formatRelativeTimestamp(telegram.lastProbeAt) : null,
              },
            ])
      }

      ${
        telegram?.lastError
          ? html`<div class="callout danger" style="margin-top: 12px;">
            ${telegram.lastError}
          </div>`
          : nothing
      }

      ${
        telegram?.probe
          ? html`<div class="callout" style="margin-top: 12px;">
            Probe ${telegram.probe.ok ? "ok" : "failed"} ·
            ${telegram.probe.status ?? ""} ${telegram.probe.error ?? ""}
          </div>`
          : nothing
      }

      ${renderChannelConfigSection({ channelId: "telegram", props })}

      <div class="row" style="margin-top: 12px;">
        <button class="btn" @click=${() => props.onRefresh(true)}>
          Probe
        </button>
      </div>
    </div>
  `;
}
